/**
 * chartTypography.jsx
 *
 * This module exports a consolidated set of typography tokens for charts
 * and application-wide use. These tokens define font families, sizes, weights,
 * colors, letter spacing, and font smoothing settings to ensure a consistent
 * look and feel across the application and its chart components.
 *
 * The tokens are organized into several groups:
 *  - global: General typography settings for the application.
 *  - axis: Specific settings for chart axes.
 *  - legend: Styling for chart legends.
 *  - tooltip: Styles for chart tooltips, including titles, labels, and values.
 *  - headings: Predefined heading styles (h1, h2, h3) and a specialized chart title.
 *
 * These tokens are designed to be scalable and easily extendable for additional
 * UI elements such as buttons or inputs if needed.
 *
 * Example usage:
 *   import { typography } from './chartTypography';
 *
 * @module chartTypography
 */

export const typography = {
    /**
     * Base font size for scalable typography across the application.
     */
    baseFontSize: '1rem',

    /**
     * Global typography settings applied throughout the application.
     */
    global: {
        fontFamily: 'Roboto Flex, sans-serif',
        fontWeight: 400,
        lineHeight: '1.5',
        color: '#1A1B25', // Default text color
        letterSpacing: '0.01rem',
        WebkitFontSmoothing: 'antialiased', // Enhances text rendering on WebKit browsers
        fontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale', // Improves text rendering on macOS Firefox
    },

    /**
     * Typography settings for chart axes.
     * Uses a monospaced font for better alignment of numerical values.
     */
    axis: {
        fontFamily: 'Roboto Mono, sans-serif',
        fontSize: '0.75rem',
        fontWeight: 400,
        color: '#444657',
        letterSpacing: '0.015rem',
        WebkitFontSmoothing: 'antialiased',
        fontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
    },

    /**
     * Typography settings for chart legends.
     * These settings include text truncation properties to handle overflow.
     */
    legend: {
        fontFamily: 'Roboto Flex, sans-serif',
        fontSize: '0.8rem',
        fontWeight: 500,
        color: '#444657',
        lineHeight: '1.25',
        letterSpacing: '0.0125rem',
        whiteSpace: 'nowrap', // Prevents line breaks within legend text
        overflow: 'hidden', // Hides overflowed text
        textOverflow: 'ellipsis', // Adds ellipsis for truncated text
        WebkitFontSmoothing: 'antialiased',
        fontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
    },

    /**
     * Typography settings for chart tooltips.
     * Contains separate configurations for the overall tooltip, title, labels, and values.
     */
    tooltip: {
        // Overall tooltip settings
        fontFamily: 'Roboto Flex, sans-serif',
        fontSize: '0.875rem',
        fontWeight: 400,
        textColor: '#1A1B25', // Text color for tooltip content
        letterSpacing: '0.01rem',

        title: {
            titleFontFamily: 'Roboto Flex, sans-serif',
            titleFontSize: '0.875rem',
            titleFontWeight: 500,
            titleColor: '#1A1B25',
            letterSpacing: '0.012rem',
        },
        label: {
            labelFontFamily: 'Roboto Flex, sans-serif',
            labelFontSize: '0.75rem',
            labelFontWeight: 400,
            labelLetterSpacing: '0.02rem',
            labelTextAlign: 'left',
            labelColor: '#626266',
        },
        value: {
            valueFontFamily: 'Roboto Mono, sans-serif',
            valueFontSize: '0.875rem',
            valueFontWeight: 500,
            valueLetterSpacing: '0.015rem',
            valueTextAlign: 'right',
            valueColor: '#1A1B25',
            valueBackgroundColor: '#F3F0F4',
        },
        WebkitFontSmoothing: 'antialiased',
        fontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
    },

    /**
     * Predefined heading styles for the application.
     * Includes standard headings (h1, h2, h3) and a specialized chart title.
     */
    headings: {
        h1: {
            fontFamily: 'Roboto Flex, sans-serif',
            fontSize: '2rem',
            fontWeight: 700,
            color: '#1A1B25',
            lineHeight: '2.5rem',
            letterSpacing: '0.015rem',
        },
        h2: {
            fontFamily: 'Roboto Flex, sans-serif',
            fontSize: '1.75rem',
            fontWeight: 600,
            color: '#1A1B25',
            lineHeight: '2.25rem',
            letterSpacing: '0.014rem',
        },
        h3: {
            fontFamily: 'Roboto Flex, sans-serif',
            fontSize: '1.5rem',
            fontWeight: 500,
            color: '#1A1B25',
            lineHeight: '2rem',
            letterSpacing: '0.0125rem',
        },
        chartTitle: {
            // Specialized heading style for chart titles
            fontFamily: 'Roboto Flex, sans-serif',
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#1A1B25',
            lineHeight: '2rem',
            letterSpacing: '0.012rem',
        },
    },

    // Additional typography tokens for other UI components (e.g., buttons, inputs)
    // can be added here as needed.
};
